import { Icon } from "../components/Icon";
import { Card, PageHead, Skeleton, NeedsData } from "../components/kit";
import { useCosts, useMargin, useUsers, useConfig, useProviderHealth } from "../lib/admin";

const HEALTH_TONE: Record<string, string> = { up: "var(--g-green)", down: "var(--g-danger)", paused: "var(--g-t3)", unknown: "var(--g-amber)" };
const HEALTH_LABEL: Record<string, string> = { up: "Operational", down: "Down", paused: "Probe off", unknown: "No data" };

export function GodDash() {
  const costs = useCosts();
  const margin = useMargin();
  const usersData = useUsers();
  const cfg = useConfig();

  if (!costs || !margin || !usersData) {
    return (
      <div className="page">
        <PageHead title="God Dashboard" sub="Live pulse across every org, user and system." />
        <div className="pulse-grid">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={92} r={14} />)}</div>
        <div className="two-col"><Skeleton h={220} r={14} /><Skeleton h={220} r={14} /></div>
      </div>
    );
  }

  const totalUsers = usersData.users.length;
  const activeOrgs = new Set(usersData.users.map((u) => u.org)).size;
  const spendToday = costs.totalCostUsd;
  const cap = cfg?.knobs.globalDailyCapUsd ?? null;
  const capPct = cap ? Math.min(100, Math.round((spendToday / cap) * 100)) : 0;

  const pulse = [
    { icon: "flame", label: "Spend today", value: `$${Math.round(spendToday).toLocaleString()}` },
    { icon: "percent", label: "Blended margin", value: margin.blended.marginPct != null ? `${margin.blended.marginPct}%` : "—" },
    { icon: "users", label: "Total users", value: totalUsers.toLocaleString() },
    { icon: "building-2", label: "Active orgs", value: String(activeOrgs) },
  ];

  return (
    <div className="page">
      <PageHead title="God Dashboard" sub="Live pulse across every org, user and system." />

      <div className="pulse-grid">
        {pulse.map((p, i) => (
          <div className="pulse" key={i}>
            <div className="pulse-top"><span className="pulse-ic"><Icon name={p.icon} size={15} /></span><span className="pulse-label">{p.label}</span></div>
            <div className="pulse-v">{p.value}</div>
          </div>
        ))}
      </div>

      <div className="two-col">
        <Card title="Needs attention today" icon="alert-triangle" iconColor="var(--g-amber)" pad={false}>
          <NeedsData icon="alert-triangle">Auto-surfaced alerts (past-due orgs, underwater margins, error spikes) need a rules pass over the live data. Not wired yet — drill in via Users / Orgs / Cost meanwhile.</NeedsData>
        </Card>

        <div className="stack-lg">
          <Card title="Global spend today" icon="flame" iconColor="var(--g-amber)">
            <div className="cap-row"><span className="cap-v">${Math.round(spendToday).toLocaleString()}</span><span className="muted-sm">of {cap != null ? `$${cap.toLocaleString()}` : "∞"} daily cap</span></div>
            <div className="cap-bar"><i style={{ width: capPct + "%" }} /></div>
            <div className="cap-foot">
              <span className="muted-sm">{cap != null ? `${capPct}% used · resets 00:00 UTC` : "No cap set"}</span>
            </div>
          </Card>
          <Card title="Provider health" icon="server" pad={false} action={<span className="muted-sm">Observability →</span>}>
            <ProviderHealthCard />
          </Card>
        </div>
      </div>
    </div>
  );
}

function ProviderHealthCard() {
  const { providers } = useProviderHealth();
  if (providers === null) {
    return <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>;
  }
  return (
    <div className="set-list">
      {providers.map((p) => (
        <div className="set-row" key={p.id}>
          <span className="set-ic" style={{ color: HEALTH_TONE[p.status] }}><span className="health-dot" style={{ background: HEALTH_TONE[p.status] }} /></span>
          <div className="set-body"><div className="set-t">{p.label}</div><div className="set-d">{HEALTH_LABEL[p.status]}{p.enabled && p.status !== "unknown" ? ` · ${p.latencyMs}ms` : ""}</div></div>
          {p.enabled && p.uptimePct != null && <span className="budget-val mono">{p.uptimePct}%</span>}
        </div>
      ))}
    </div>
  );
}
